import { useEffect, useMemo, useRef, useState } from "react";
import { generateClient, post } from "aws-amplify/api";
import { fetchAuthSession } from "aws-amplify/auth";
import { uploadData } from "aws-amplify/storage";
import { useAuth } from "../AuthProvider";
import {
  createIDRCase,
  getIDRAnalysisRun,
  listIDRAnalysisRunsByCase,
  listIDRCases,
  updateIDRCase,
} from "../graphql/idrCase";
import {
  DOCUMENT_ROLES,
  IDR_CASE_SCHEMA_VERSION,
  createEmptyIdrCase,
  getBlockingIssues,
  getIDRServiceLines,
  getPricingOfferIssues,
  parsePageAssignments,
  validateIdrCaseData,
  validateIdrManifest,
} from "../features/idr/idrWorkflow";
import "./IDRWorkflowScreen.css";

const IDR_API_NAME = process.env.REACT_APP_CODING_API_NAME || "accountRequestsAPI";
const IDR_ANALYSIS_PATH = process.env.REACT_APP_IDR_ANALYSIS_PATH || "/idr/analyze";
const IDR_CANCEL_PATH = process.env.REACT_APP_IDR_CANCEL_PATH || "/idr/cancel";
const IDR_DELETE_PATH = process.env.REACT_APP_IDR_DELETE_PATH || "/idr/delete";
const MAX_DOCUMENTS = 10;
const MAX_IMAGES = 6;
const MAX_TOTAL_BYTES = 24 * 1024 * 1024;
const SUPPORTED_EXTENSIONS = new Set([
  "pdf",
  "txt",
  "docx",
  "rtf",
  "md",
  "png",
  "jpg",
  "jpeg",
  "webp",
]);

function newId() {
  return window.crypto?.randomUUID?.() || `idr-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function cleanFileName(name) {
  return String(name || "document")
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, "")
    .replace(/[\\/]+/g, "_")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 160);
}

function getFileExtension(name) {
  return String(name || "").split(".").pop().toLowerCase();
}

function parseOptionalJson(value, label) {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    throw new Error(`${label} is not valid JSON.`);
  }
}

function contentType(file) {
  if (file.type) return file.type;
  const extension = getFileExtension(file.name);
  if (extension === "pdf") return "application/pdf";
  if (["png", "jpg", "jpeg", "webp"].includes(extension)) {
    return extension === "jpg" ? "image/jpeg" : `image/${extension}`;
  }
  if (extension === "txt") return "text/plain";
  if (extension === "docx") {
    return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  }
  if (extension === "rtf") return "application/rtf";
  if (extension === "md") return "text/markdown";
  return "application/octet-stream";
}

function toHex(buffer) {
  return Array.from(new Uint8Array(buffer), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

async function hashFile(file) {
  if (!window.crypto?.subtle) {
    throw new Error("This browser cannot create a source hash. Use a current browser over HTTPS.");
  }
  return toHex(await window.crypto.subtle.digest("SHA-256", await file.arrayBuffer()));
}

function compressPages(pages) {
  const sorted = [...new Set((pages || []).map(Number))].sort((a, b) => a - b);
  if (!sorted.length) return "";
  const ranges = [];
  let start = sorted[0];
  let end = sorted[0];
  for (const page of sorted.slice(1)) {
    if (page === end + 1) {
      end = page;
    } else {
      ranges.push(start === end ? `${start}` : `${start}-${end}`);
      start = page;
      end = page;
    }
  }
  ranges.push(start === end ? `${start}` : `${start}-${end}`);
  return ranges.join(",");
}

function requireGraphqlData(response, key) {
  if (response?.errors?.length) {
    throw new Error(response.errors.map((item) => item.message).filter(Boolean).join("; "));
  }
  if (!response?.data?.[key]) throw new Error(`The server returned no ${key} record.`);
  return response.data[key];
}

function editableFingerprint(idrCase, manifest) {
  const { draftMarkdown, draftInputFingerprint, draftReviewNotes, reviewHistory, ...inputs } = idrCase;
  return JSON.stringify({
    inputs,
    sources: manifest.map((document) => {
      const pageCount = String(document.pageCount || "").trim()
        ? Number(document.pageCount)
        : null;
      const pageAssignments = document.pageAssignmentsText
        ? parsePageAssignments(document.pageAssignmentsText, pageCount).assignments
        : document.pageAssignments || [];
      return {
        id: document.id,
        version: document.version,
        name: document.name,
        role: document.role,
        sizeBytes: document.sizeBytes,
        mimeType: document.mimeType,
        pageCount,
        pageAssignments,
        hash: document.hash,
        storageKey: document.storageKey,
      };
    }),
  });
}

function SourceUploadButton({ label, onChange }) {
  const inputRef = useRef(null);
  return (
    <div className="idr-file-picker">
      <input
        ref={inputRef}
        className="idr-upload-input"
        aria-label={label}
        type="file"
        multiple
        accept=".pdf,.txt,.docx,.rtf,.md,.png,.jpg,.jpeg,.webp"
        onChange={onChange}
        tabIndex={-1}
      />
      <button type="button" className="button" onClick={() => inputRef.current.click()}>
        {label}
      </button>
    </div>
  );
}

function TextField({ label, value, onChange, rows = 3, placeholder, maxLength }) {
  return (
    <label className="idr-field idr-field-wide">
      <span>{label}</span>
      <textarea
        rows={rows}
        value={value || ""}
        placeholder={placeholder}
        maxLength={maxLength}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

export default function IDRWorkflowScreen({ onExit }) {
  const { user } = useAuth();
  const client = useMemo(() => generateClient(), []);
  const [cases, setCases] = useState([]);
  const [caseId, setCaseId] = useState("");
  const [artifactVersion, setArtifactVersion] = useState(0);
  const [idrCase, setIdrCase] = useState(createEmptyIdrCase);
  const [manifest, setManifest] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisCancelLoading, setAnalysisCancelLoading] = useState(false);
  const [analysisStatus, setAnalysisStatus] = useState(null);

  const analysisStatusRef = useRef(null);

  const [analysisRuns, setAnalysisRuns] = useState([]);
  const [analysisRunsNextToken, setAnalysisRunsNextToken] = useState(null);
  const [analysisRunDetails, setAnalysisRunDetails] = useState({});
  const [loadingAnalysisRunId, setLoadingAnalysisRunId] = useState("");
  const [deletingCase, setDeletingCase] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");

  const blockers = useMemo(() => getBlockingIssues(idrCase), [idrCase]);
  const currentFingerprint = useMemo(
    () => editableFingerprint(idrCase, manifest),
    [idrCase, manifest]
  );

  const pricingOfferIssues = getPricingOfferIssues(idrCase);
  const hasPricingSources = manifest.some((document) => {
    const assignments = document.pageAssignmentsText
      ? parsePageAssignments(document.pageAssignmentsText, Number(document.pageCount) || null).assignments
      : document.pageAssignments;
    const roles = assignments?.length ? assignments.map((assignment) => assignment.role) : [document.role];
    return roles.some((role) =>
      ["TARGET_CLAIM_REMITTANCE", "COMPARATOR_EOB", "COMPARATOR_AWARD_AGREEMENT", "BENCHMARK", "PAYER_SUBMISSION"].includes(role)
    );
  });

  const listedCases = useMemo(
    () => [...cases].sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)),
    [cases]
  );

  const setCaseField = (section, key, value) => {
    setIdrCase((previous) => ({
      ...previous,
      [section]: { ...previous[section], [key]: value },
    }));
  };

  const loadCases = async () => {
    if (!user?.username) return;
    setLoading(true);
    setError("");
    try {
      const items = [];
      let nextToken = null;
      do {
        const response = await client.graphql({
          query: listIDRCases,
          variables: {
            filter: { userId: { eq: user.username } },
            limit: 100,
            nextToken,
          },
        });
        if (response?.errors?.length) {
          throw new Error(response.errors.map((item) => item.message).filter(Boolean).join("; "));
        }
        items.push(...(response?.data?.listIDRCases?.items || []).filter(Boolean));
        nextToken = response?.data?.listIDRCases?.nextToken || null;
      } while (nextToken);
      setCases(items);
      const selected = items.find((item) => item.id === caseId);
      if (selected) {
        const savedStatus = parseOptionalJson(selected.analysisStatusJson, "Analysis status");
        const currentRunId = analysisStatusRef.current?.runId;
        if (!currentRunId || savedStatus?.runId === currentRunId) {
          setAnalysisStatus(savedStatus);
        }
      }
    } catch (loadError) {
      setError(`Could not load IDR cases: ${loadError.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalysisRuns = async (nextToken = null, append = false) => {
    if (!caseId) {
      setAnalysisRuns([]);
      setAnalysisRunsNextToken(null);
      return;
    }
    try {
      const response = await client.graphql({
        query: listIDRAnalysisRunsByCase,
        variables: {
          caseId,
          sortDirection: "DESC",
          limit: 25,
          nextToken,
        },
      });
      if (response?.errors?.length) {
        throw new Error(response.errors.map((item) => item.message).filter(Boolean).join("; "));
      }
      const result = response?.data?.listIDRAnalysisRunsByCase;
      setAnalysisRuns((previous) =>
        append ? [...previous, ...(result?.items || []).filter(Boolean)] : (result?.items || []).filter(Boolean)
      );
      setAnalysisRunsNextToken(result?.nextToken || null);
    } catch (historyError) {
      setError(`Could not load immutable analysis history: ${historyError.message}`);
    }
  };

  const loadAnalysisRunDetails = async (runId) => {
    if (analysisRunDetails[runId]) {
      setAnalysisRunDetails((previous) => {
        const next = { ...previous };
        delete next[runId];
        return next;
      });
      return;
    }
    setLoadingAnalysisRunId(runId);
    try {
      const response = await client.graphql({
        query: getIDRAnalysisRun,
        variables: { id: runId },
      });
      if (response?.errors?.length) {
        throw new Error(response.errors.map((item) => item.message).filter(Boolean).join("; "));
      }
      const result = response?.data?.getIDRAnalysisRun;
      if (!result || result.caseId !== caseId) {
        throw new Error("The archived run does not belong to the selected case.");
      }
      setAnalysisRunDetails((previous) => ({ ...previous, [runId]: result }));
    } catch (detailError) {
      setError(`Could not load archived analysis run: ${detailError.message}`);
    } finally {
      setLoadingAnalysisRunId("");
    }
  };

  useEffect(() => {
    analysisStatusRef.current = analysisStatus;
  }, [analysisStatus]);

  useEffect(() => {
    loadCases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.username]);

  useEffect(() => {
    setAnalysisRuns([]);
    setAnalysisRunsNextToken(null);
    setAnalysisRunDetails({});
    if (caseId) loadAnalysisRuns();
    // A new case selection loads its separately owner-authorized run history.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, user?.username]);

  useEffect(() => {
    if (!["QUEUED", "RUNNING"].includes(analysisStatus?.status)) return undefined;
    const timer = window.setInterval(() => {
      loadCases();
      loadAnalysisRuns();
    }, 4000);
    return () => window.clearInterval(timer);
    // Poll the selected owner-scoped case while its backend run is active.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisStatus?.status, caseId, user?.username]);

  const openCase = (record) => {
    try {
      const savedCase = JSON.parse(record.workflowJson);
      const parsedCase = {
        ...savedCase,
        intakeMode: "free_text",
        offerNarrative: savedCase.offerNarrative || "",
        outputMode: savedCase.outputMode || "pricing_statement",
        serviceLines: getIDRServiceLines(savedCase),
        payments: {
          ...createEmptyIdrCase().payments,
          ...(savedCase.payments || {}),
        },
        draftReviewNotes: savedCase.draftReviewNotes || [],
      };
      const parsedManifest = JSON.parse(record.manifestJson).map((document) => ({
        ...document,
        pageAssignmentsText: (document.pageAssignments || [])
          .map((assignment) => `${compressPages(assignment.pages)}=${assignment.role}`)
          .join("\n"),
      }));
      const validationErrors = [
        ...validateIdrCaseData(parsedCase),
        ...validateIdrManifest(parsedManifest),
      ];
      if (validationErrors.length) {
        throw new Error(validationErrors.join(" "));
      }
      setIdrCase(parsedCase);
      setManifest(parsedManifest);
      setCaseId(record.id);
      setArtifactVersion(Number(record.artifactVersion || 0));
      const savedAnalysisStatus = parseOptionalJson(record.analysisStatusJson, "Analysis status");
      setAnalysisStatus(savedAnalysisStatus);
      setStatus(`Opened ${record.caseName || "IDR case"}.`);
      setError("");
    } catch (parseError) {
      setError(`Could not open case: ${parseError.message}`);
    }
  };

  const startNewCase = () => {
    setIdrCase(createEmptyIdrCase());
    setManifest([]);
    setCaseId("");
    setArtifactVersion(0);
    setAnalysisStatus(null);

    setAnalysisRuns([]);
    setAnalysisRunsNextToken(null);

    setStatus("New case. Upload your sources and describe your IDR offer.");
    setError("");
  };

  const addFiles = (event, assignedRole) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    const totalCount = manifest.length + files.length;
    const totalBytes = manifest.reduce((sum, document) => sum + Number(document.sizeBytes || 0), 0) +
      files.reduce((sum, file) => sum + file.size, 0);
    const imageCount = manifest.filter((document) => document.isImage).length +
      files.filter((file) => ["png", "jpg", "jpeg", "webp"].includes(getFileExtension(file.name))).length;
    const unsupported = files.filter((file) => !SUPPORTED_EXTENSIONS.has(getFileExtension(file.name)));
    if (unsupported.length) {
      setError(`Unsupported file type(s): ${unsupported.map((file) => file.name).join(", ")}.`);
      return;
    }
    if (totalCount > MAX_DOCUMENTS || imageCount > MAX_IMAGES || totalBytes > MAX_TOTAL_BYTES) {
      setError(`Upload limit exceeded: maximum ${MAX_DOCUMENTS} documents, ${MAX_IMAGES} images, and 24 MiB total.`);
      return;
    }
    setError("");
    setManifest((previous) => [
      ...previous,
      ...files.map((file) => ({
        id: newId(),
        version: 1,
        name: file.name,
        role: assignedRole,
        sizeBytes: file.size,
        mimeType: contentType(file),
        pageCount: "",
        pageAssignments: [],
        pageAssignmentsText: "",
        isImage: ["png", "jpg", "jpeg", "webp"].includes(getFileExtension(file.name)),
        hash: "",
        storageKey: "",
        extractionStatus: "NOT_RUN",
        pageStatus: "NOT_PROCESSED",
        pendingFile: file,
      })),
    ]);
  };

  const validateManifest = () => {
    for (const document of manifest) {
      const pageCountText = String(document.pageCount || "").trim();
      if (
        pageCountText &&
        (!Number.isInteger(Number(pageCountText)) || Number(pageCountText) < 1 || Number(pageCountText) > 10000)
      ) {
        return `${document.name}: enter a recorded page count from 1 to 10,000, or leave it unknown.`;
      }
      const parsed = parsePageAssignments(
        document.pageAssignmentsText || "",
        document.pageCount || null
      );
      if (parsed.errors.length) {
        return `${document.name}: ${parsed.errors.join(" ")}`;
      }
      if (!DOCUMENT_ROLES.includes(document.role)) {
        return `${document.name}: select a supported document role.`;
      }
    }
    return "";
  };

  const saveCase = async () => {
    if (!user?.username) {
      setError("You must be signed in to save an IDR case.");
      return;
    }
    const manifestError = validateManifest();
    if (manifestError) {
      setError(manifestError);

      return;
    }
    const caseValidationErrors = validateIdrCaseData(idrCase);
    if (caseValidationErrors.length) {
      setError(`Case data is invalid: ${caseValidationErrors.join(" ")}`);
      return;
    }

    setSaving(true);
    setError("");
    setStatus("Uploading private evidence and saving the case...");
    const nextCaseId = caseId || `idr-${newId()}`;
    const priorCase = cases.find((item) => item.id === nextCaseId);
    const uploadedManifest = [];
    try {
      const authSession = await fetchAuthSession();
      const currentStorageIdentityId = String(authSession?.identityId || "").trim();
      if (manifest.length && !currentStorageIdentityId) {
        throw new Error("Private storage identity is unavailable. Sign in again before saving uploaded sources.");
      }
      if (
        priorCase?.storageIdentityId &&
        currentStorageIdentityId &&
        priorCase.storageIdentityId !== currentStorageIdentityId
      ) {
        throw new Error("The private storage identity changed. Reopen the case with its original account before saving.");
      }
      const storageIdentityId = priorCase?.storageIdentityId || currentStorageIdentityId || null;
      for (const document of manifest) {
        const { pendingFile, pageAssignmentsText, ...persisted } = document;
        const pageCount = String(document.pageCount || "").trim()
          ? Number(document.pageCount)
          : null;
        const pageAssignments = parsePageAssignments(
          pageAssignmentsText || "",
          pageCount
        ).assignments;
        let storageKey = persisted.storageKey;
        let hash = persisted.hash;
        if (pendingFile && !storageKey) {
          hash = await hashFile(pendingFile);
          storageKey = `${user.username}/idr-docs/${nextCaseId}/${document.id}/${cleanFileName(document.name)}`;
          const uploaded = await uploadData({
            key: storageKey,
            data: pendingFile,
            options: {
              accessLevel: "private",
              contentType: document.mimeType,
            },
          }).result;
          storageKey = uploaded?.key || storageKey;
        }
        uploadedManifest.push({
          ...persisted,
          pageCount,
          pageAssignments,
          hash,
          storageKey,
          uploadedAt: storageKey ? persisted.uploadedAt || new Date().toISOString() : null,
        });
      }
      const manifestValidationErrors = validateIdrManifest(uploadedManifest);
      if (manifestValidationErrors.length) {
        throw new Error(manifestValidationErrors.join(" "));
      }

      const previousWorkflow = priorCase?.workflowJson ? JSON.parse(priorCase.workflowJson) : null;
      const changedFields = [];
      if (previousWorkflow) {
        ["metadata", "offer", "offerNarrative", "clinicalCustomPrompt", "intakeMode", "payments", "benchmark", "clinicalFindings", "comparators", "legalSources", "payerArguments"]
          .forEach((key) => {
            if (JSON.stringify(previousWorkflow[key]) !== JSON.stringify(idrCase[key])) {
              changedFields.push(key);
            }
          });
      }
      const caseToSave = {
        ...idrCase,
        schemaVersion: IDR_CASE_SCHEMA_VERSION,
        reviewHistory: changedFields.length
          ? [
              ...(idrCase.reviewHistory || []),
              {
                author: user.username,
                timestamp: new Date().toISOString(),
                source: "USER_EDIT",
                changedFields,
              },
            ]
          : idrCase.reviewHistory || [],
      };

      const sourceInputsChanged = !previousWorkflow || editableFingerprint(previousWorkflow, JSON.parse(priorCase.manifestJson)) !== editableFingerprint(caseToSave, uploadedManifest);
      const nextVersion = sourceInputsChanged ? artifactVersion + 1 : artifactVersion;
      const hasBlockingIssues = getBlockingIssues(caseToSave).length > 0;
      const hasCurrentDraft =
        Boolean(caseToSave.draftMarkdown) &&
        caseToSave.draftInputFingerprint === editableFingerprint(caseToSave, uploadedManifest);
      const staleAnalysisStatus = analysisStatus && sourceInputsChanged
        ? {
            ...analysisStatus,
            status: "STALE",
            updatedAt: new Date().toISOString(),
            clinical: analysisStatus.clinical
              ? { ...analysisStatus.clinical, status: "STALE" }
              : undefined,
            pricing: analysisStatus.pricing
              ? { ...analysisStatus.pricing, status: "STALE" }
              : undefined,
            legalResearch: analysisStatus.legalResearch
              ? { ...analysisStatus.legalResearch, status: "STALE" }
              : undefined,
          }
        : analysisStatus;
      const input = {
        id: nextCaseId,
        userId: user.username,
        caseName: String(
          caseToSave.metadata.disputeId ||
          caseToSave.metadata.targetIdentity ||
          uploadedManifest.find((document) => document.role === "TARGET_CLAIM_REMITTANCE")?.name ||
          uploadedManifest[0]?.name ||
          "Untitled IDR case"
        ).trim().slice(0, 120),
        storageIdentityId,
        processingStatus: hasBlockingIssues
          ? "NEEDS_INPUT"
          : !caseToSave.draftMarkdown
            ? "INTAKE_REVIEW"
            : hasCurrentDraft
              ? "DRAFT_REVIEW"
              : "DRAFT_STALE",
        workflowJson: JSON.stringify(caseToSave),
        manifestJson: JSON.stringify(uploadedManifest),
        draftMarkdown: caseToSave.draftMarkdown || null,
        artifactVersion: nextVersion,
        analysisRunStatus: staleAnalysisStatus?.status || null,
        analysisStatusJson: staleAnalysisStatus ? JSON.stringify(staleAnalysisStatus) : null,
      };
      const variables = { input };
      if (caseId) {
        variables.condition = {
          artifactVersion: { eq: artifactVersion },
          processingStatus: { ne: "DELETING" },
        };
      }
      const response = await client.graphql({
        query: caseId ? updateIDRCase : createIDRCase,
        variables,
      });
      const saved = requireGraphqlData(
        response,
        caseId ? "updateIDRCase" : "createIDRCase"
      );
      setCaseId(saved.id);
      setArtifactVersion(Number(saved.artifactVersion || nextVersion));
      setAnalysisStatus(staleAnalysisStatus);

      setIdrCase(caseToSave);
      setManifest(uploadedManifest.map((document) => ({
        ...document,
        pageAssignmentsText: (document.pageAssignments || [])
          .map((assignment) => `${compressPages(assignment.pages)}=${assignment.role}`)
          .join("\n"),
      })));
      setStatus(`Saved artifact version ${saved.artifactVersion || nextVersion} (${input.processingStatus}). This is not an approved or filed offer.`);
      await loadCases();
    } catch (saveError) {
      setError(`Could not save IDR case: ${saveError.message}`);
      setStatus("");
    } finally {
      setSaving(false);
    }
  };

  const runIDRAnalysis = async () => {
    if (hasPricingSources && pricingOfferIssues.length) {
      setError(pricingOfferIssues.join(" "));

      return;
    }
    if (!caseId || !artifactVersion) {
      setError("Save this case and its uploaded documents before starting analysis.");

      return;
    }
    if (manifest.some((document) => document.pendingFile || !document.storageKey)) {
      setError("Save and upload every source document before starting analysis.");

      return;
    }
    if (manifest.some((document) => getFileExtension(document.name) === "webp")) {
      setError("Convert WebP images to PNG or JPEG before starting OCR.");

      return;
    }
    const selectedCase = cases.find((item) => item.id === caseId);
    if (!selectedCase) {
      setError("Refresh the saved cases before starting analysis.");
      setStatus("");
      return;
    }
    let savedWorkflow;
    let savedManifest;
    try {
      savedWorkflow = parseOptionalJson(selectedCase.workflowJson, "Saved case");
      savedManifest = JSON.parse(selectedCase.manifestJson);
    } catch (savedCaseError) {
      setError(`Could not verify saved case settings: ${savedCaseError.message}`);
      setStatus("");
      return;
    }
    const currentJurisdiction = String(idrCase.metadata?.jurisdiction || "")
      .trim()
      .toLocaleUpperCase("en-US");
    const savedJurisdiction = String(savedWorkflow?.metadata?.jurisdiction || "")
      .trim()
      .toLocaleUpperCase("en-US");
    if (
      savedWorkflow?.research?.enabled !== Boolean(idrCase.research?.enabled) ||
      (idrCase.research?.enabled && currentJurisdiction !== savedJurisdiction)
    ) {
      setError("Save the legal-research setting and jurisdiction before starting analysis.");
      setStatus("");
      return;
    }
    if (editableFingerprint(savedWorkflow, savedManifest) !== currentFingerprint) {
      setError("Save your IDR offer and uploaded source changes before starting analysis.");

      return;
    }
    setAnalysisLoading(true);
    setError("");
    setStatus("Queueing OCR, separate clinical/pricing analyses, and enabled legal research...");
    try {
      const authSession = await fetchAuthSession();
      const accessToken = authSession?.tokens?.accessToken?.toString?.() || "";
      const idToken = authSession?.tokens?.idToken?.toString?.() || "";
      if (!accessToken || !idToken) throw new Error("Your sign-in session is unavailable. Sign in again.");
      const response = await post({
        apiName: IDR_API_NAME,
        path: IDR_ANALYSIS_PATH,
        options: {
          body: { caseId, artifactVersion, idToken, accessToken },
          headers: {
            "Content-Type": "application/json",
          },
        },
      }).response;
      const httpStatus = Number(response?.statusCode || response?.status || 200);
      const payload = await response.body.json();
      if (httpStatus >= 400 || payload?.ok === false) {
        throw new Error(payload?.error || `Analysis could not be started (${httpStatus}).`);
      }
      setAnalysisStatus(payload.analysisStatus || null);

      setStatus(
        payload.status === "QUEUED" || payload.status === "RUNNING"
          ? "OCR and analysis queued. Results require human review."
          : `Analysis status: ${payload.status || "unknown"}.`
      );
    } catch (analysisError) {
      setError(`Could not start IDR analysis: ${analysisError.message}`);
      setStatus("");
    } finally {
      setAnalysisLoading(false);
    }
  };

  const cancelIDRAnalysis = async () => {
    if (!caseId || !analysisStatus?.runId || analysisCancelLoading) return;
    const confirmed = window.confirm(
      "Cancel this IDR analysis? Any in-flight provider request may finish, but its result will not be saved."
    );
    if (!confirmed) return;
    setAnalysisCancelLoading(true);
    setError("");
    try {
      const authSession = await fetchAuthSession();
      const accessToken = authSession?.tokens?.accessToken?.toString?.() || "";
      if (!accessToken) throw new Error("Your sign-in session is unavailable. Sign in again.");
      const response = await post({
        apiName: IDR_API_NAME,
        path: IDR_CANCEL_PATH,
        options: {
          body: { caseId, runId: analysisStatus.runId, accessToken },
          headers: {
            "Content-Type": "application/json",
          },
        },
      }).response;
      const httpStatus = Number(response?.statusCode || response?.status || 200);
      const payload = await response.body.json();
      if (httpStatus >= 400 || payload?.ok !== true) {
        throw new Error(payload?.error || `Cancellation could not be saved (${httpStatus}).`);
      }
      const cancelledStatus = payload.analysisStatus || { ...analysisStatus, status: "CANCELLED" };
      setAnalysisStatus(cancelledStatus);
      setCases((previous) => previous.map((record) =>
        record.id === caseId
          ? {
              ...record,
              analysisRunStatus: "CANCELLED",
              analysisStatusJson: JSON.stringify(cancelledStatus),
            }
          : record
      ));
      setStatus(
        payload.message ||
        "Cancellation recorded. In-flight provider work may finish, but results will not be saved."
      );
      await loadAnalysisRuns();
    } catch (cancelError) {
      setError(`Could not cancel IDR analysis: ${cancelError.message}`);
      setStatus("");
    } finally {
      setAnalysisCancelLoading(false);
    }
  };

  const deleteCaseAndHistory = async () => {
    if (!caseId || deletingCase) return;
    const confirmed = window.confirm(
      "Delete this IDR case and all of its saved analysis history? This cannot be undone."
    );
    if (!confirmed) return;
    setDeletingCase(true);
    setError("");
    setStatus("Deleting the case and its run history...");
    try {
      const authSession = await fetchAuthSession();
      const accessToken = authSession?.tokens?.accessToken?.toString?.() || "";
      const idToken = authSession?.tokens?.idToken?.toString?.() || "";
      if (!accessToken || !idToken) throw new Error("Your sign-in session is unavailable. Sign in again.");
      const response = await post({
        apiName: IDR_API_NAME,
        path: IDR_DELETE_PATH,
        options: {
          body: { caseId, idToken, accessToken },
          headers: { "Content-Type": "application/json" },
        },
      }).response;
      const httpStatus = Number(response?.statusCode || response?.status || 200);
      const payload = await response.body.json();
      if (httpStatus >= 400 || payload?.ok === false) {
        throw new Error(payload?.error || `Case deletion failed (${httpStatus}).`);
      }
      startNewCase();
      setStatus("IDR case, private uploads, analysis history, and approval history deleted.");
      await loadCases();
    } catch (deleteError) {
      setError(`Could not delete IDR case: ${deleteError.message}`);
      setStatus("");
    } finally {
      setDeletingCase(false);
    }
  };

  return (
    <main className="container idr-workspace">
      <header className="idr-header">
        <div>
          <h1>IDR offer preparation</h1>
          <p>Upload your case sources and describe your IDR offer. No dispute is filed or sent.</p>
        </div>
        <div className="idr-header-actions">
          {onExit && (
            <button type="button" className="button" onClick={onExit}>Back to LOMN</button>
          )}
          <button type="button" className="button" onClick={startNewCase}>New case</button>
          {caseId && (
            <button
              type="button"
              className="button"
              onClick={deleteCaseAndHistory}
              disabled={deletingCase || ["QUEUED", "RUNNING"].includes(analysisStatus?.status)}
            >
              {deletingCase ? "Deleting..." : "Delete case and history"}
            </button>
          )}
          <button type="button" className="button save" onClick={saveCase} disabled={saving}>
            {saving ? "Saving..." : "Save case"}
          </button>
        </div>
      </header>

      <aside className="idr-boundary-note">
        OCR and separate clinical/pricing model extraction can be queued for saved cases after the backend schema and model configuration are deployed. Results are retained in version-bound analysis history. No document is filed, sent, or approved from this screen. Saved case activity is subject to seven-year retention cleanup after deployment.
      </aside>

      {error && <div role="alert" className="idr-alert idr-alert-error">{error}</div>}
      {status && <div role="status" className="idr-alert idr-alert-status">{status}</div>}

      <section className="idr-case-picker" aria-label="Saved IDR cases">
        <label>
          <span>Open a saved case</span>
          <select
            value={caseId}
            onChange={(event) => {
              const selected = cases.find((record) => record.id === event.target.value);
              if (selected) openCase(selected);
            }}
          >
            <option value="">Select saved case...</option>
            {listedCases.map((record) => (
              <option key={record.id} value={record.id}>
                {record.caseName} · {record.processingStatus} · v{record.artifactVersion}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="button" onClick={loadCases} disabled={loading}>
          {loading ? "Refreshing..." : "Refresh cases"}
        </button>
        {caseId && <span>Artifact version {artifactVersion}</span>}
      </section>

      <nav className="idr-tabs" aria-label="IDR workflow stages">
        <button type="button" className="is-active" aria-current="step">
          Case &amp; sources
        </button>
      </nav>

      {caseId && (
        <section className="idr-panel idr-analysis-history" aria-label="Immutable analysis history">
          <h2>Version-bound analysis history</h2>
          <p>Each run keeps its original case inputs and outputs. Editing creates a new artifact version; deleting the case removes its run history.</p>
          {analysisRuns.length === 0 ? (
            <p className="idr-muted">No saved analysis runs.</p>
          ) : (
            <ol>
              {analysisRuns.map((run) => {
                let savedStatus = { status: run.status };
                try {
                  savedStatus = JSON.parse(run.statusJson || "{}");
                } catch {
                  savedStatus = { status: run.status, statusJsonInvalid: true };
                }
                return (
                  <li key={run.id}>
                    <strong>Version {run.artifactVersion} · {savedStatus.status || run.status}</strong>
                    <span> · {new Date(run.createdAt).toLocaleString()} · run {run.runId}</span>
                    <small>Input SHA-256: <code>{run.inputHash}</code></small>
                      <button
                        type="button"
                        className="button"
                        onClick={() => loadAnalysisRunDetails(run.id)}
                        disabled={loadingAnalysisRunId === run.id}
                      >
                        {loadingAnalysisRunId === run.id
                          ? "Loading archived run..."
                          : analysisRunDetails[run.id]
                            ? "Hide archived run"
                            : "View archived inputs and outputs"}
                      </button>
                      {analysisRunDetails[run.id] && (
                        <pre>{JSON.stringify({
                          schemaVersion: analysisRunDetails[run.id].schemaVersion,
                          status: analysisRunDetails[run.id].statusJson,
                          workflowSnapshotJson: analysisRunDetails[run.id].workflowSnapshotJson,
                          manifestSnapshotJson: analysisRunDetails[run.id].manifestSnapshotJson,
                          clinicalAnalysisJson: analysisRunDetails[run.id].clinicalAnalysisJson,
                          pricingAnalysisJson: analysisRunDetails[run.id].pricingAnalysisJson,
                          legalResearchJson: analysisRunDetails[run.id].legalResearchJson,
                        }, null, 2)}</pre>
                      )}
                  </li>
                );
              })}
            </ol>
          )}
          {analysisRunsNextToken && (
            <button
              type="button"
              className="button"
              onClick={() => loadAnalysisRuns(analysisRunsNextToken, true)}
            >
              Load older runs
            </button>
          )}
        </section>
      )}

      <section className="idr-panel">
          <h2>Uploads and IDR offer</h2>

          <section className="idr-subsection">
            <h3>Medical notes for medical-necessity analysis</h3>
            <p>Upload the target patient's medical notes here. They are not sent to the pricing model.</p>
            <SourceUploadButton label="Upload Medical Notes" onChange={(event) => addFiles(event, "TARGET_CLINICAL_RECORD")} />
            <p>{manifest.filter((document) => document.role === "TARGET_CLINICAL_RECORD").map((document) => document.name).join(", ") || "No medical notes attached."}</p>
            <TextField
              label="Custom prompt for medical analysis (optional)"
              value={idrCase.clinicalCustomPrompt}
              rows={4}
              maxLength={5000}
              placeholder="For example: Focus on prior treatments, functional limitations, and the documented reasons for this procedure."
              onChange={(value) => setIdrCase((previous) => ({
                ...previous, clinicalCustomPrompt: value,
              }))}
            />
            <p className="idr-muted">Applies only to medical-note analysis, not pricing or legal research. Save changes before analyzing. Maximum 5,000 characters.</p>
          </section>

          <section className="idr-subsection" aria-label="EOB pricing uploads">
            <h3>EOB data for pricing analysis</h3>
            <p>Upload the disputed case EOB with its QPA separately from example payments. Line items and their QPA will be extracted from the case EOB and retained in the saved analysis run.</p>
            <TextField
              label="Your IDR offer (required before pricing analysis)"
              value={idrCase.offerNarrative}
              rows={5}
              placeholder="Describe your offer in your own words, including the amount and services it covers."
              onChange={(value) => setIdrCase((previous) => ({
                ...previous, intakeMode: "free_text", offerNarrative: value,
              }))}
            />
            {pricingOfferIssues.length > 0 && (
              <ul className="idr-issue-list">{pricingOfferIssues.map((issue) => <li key={issue}>{issue}</li>)}</ul>
            )}
            <div className="idr-upload-controls">
              <SourceUploadButton label="Case EOB QPA" onChange={(event) => addFiles(event, "TARGET_CLAIM_REMITTANCE")} />
              <SourceUploadButton label="Example Payments" onChange={(event) => addFiles(event, "COMPARATOR_EOB")} />
            </div>
            <p>Case EOB QPA: {manifest.filter((document) => document.role === "TARGET_CLAIM_REMITTANCE").map((document) => document.name).join(", ") || "No case EOB attached."}</p>
            <p>Example Payments: {manifest.filter((document) => document.role === "COMPARATOR_EOB").map((document) => document.name).join(", ") || "No example payments attached."}</p>
          </section>

          <p className="idr-muted">Maximum {MAX_DOCUMENTS} documents, {MAX_IMAGES} images, 24 MiB total.</p>
        </section>

      <footer className="idr-workflow-footer">
        <span>{blockers.length ? "Describe your IDR offer before pricing analysis." : "Offer entered. Save your case before starting analysis."}</span>
        <button type="button" className="button save" onClick={saveCase} disabled={saving}>
          {saving ? "Saving..." : "Save case"}
        </button>
      </footer>

      <section className="idr-analysis-controls" aria-label="OCR and model analysis">
        <div className="idr-analysis-summary">
          <h2>Run case analysis</h2>
          <p>After uploading your documents, adding your IDR offer, and saving the case above, start analysis here.</p>
          <ul>
            <li><strong>Medical notes:</strong> analyze medical-necessity evidence using your optional custom prompt.</li>
            <li><strong>EOBs:</strong> extract case QPA and compare example payments in a separate pricing analysis.</li>
          </ul>
          <p className="idr-muted">Results are saved in analysis history and require human review. Nothing is filed or sent to an IDR entity.</p>
        </div>
        <div className="idr-analysis-status" aria-live="polite">
          {analysisStatus && (
            <p>
              Run {analysisStatus.status || "UNKNOWN"}; clinical{" "}
              {analysisStatus.clinical?.status || "—"}
              {analysisStatus.clinical?.errorCode ? ` (${analysisStatus.clinical.errorCode})` : ""}
              ; pricing {analysisStatus.pricing?.status || "—"}
              {analysisStatus.pricing?.errorCode ? ` (${analysisStatus.pricing.errorCode})` : ""}.
              {idrCase.research?.enabled
                ? ` Legal research ${analysisStatus.legalResearch?.status || "NOT_RUN"}${analysisStatus.legalResearch?.errorCode ? ` (${analysisStatus.legalResearch.errorCode})` : ""}.`
                : " Legal research is disabled."}
              {Number(analysisStatus.artifactVersion) !== artifactVersion
                ? " These results are stale for the current artifact version."
                : ""}
            </p>
          )}
        </div>
        <details className="idr-analysis-options" aria-label="Optional legal research settings">
          <summary>Optional legal research</summary>
          <label className="idr-inline-control">
            <input
              type="checkbox"
              checked={Boolean(idrCase.research?.enabled)}
              onChange={(event) => setCaseField("research", "enabled", event.target.checked)}
            />
            <span>Include federal legal research</span>
          </label>
          <p>Off by default. Searches official government sources separately, without patient details or document text. Save this option before starting analysis.</p>
        </details>
        <div className="idr-analysis-actions" role="group" aria-label="Analysis actions">
        <button
          type="button"
          className="button"
          onClick={runIDRAnalysis}
          disabled={
            !caseId ||
            saving ||
            (hasPricingSources && pricingOfferIssues.length > 0) ||
            analysisLoading ||
            ["QUEUED", "RUNNING"].includes(analysisStatus?.status) ||
            (Number(analysisStatus?.artifactVersion) === artifactVersion &&
              analysisStatus?.status === "COMPLETED")
          }
        >
          {analysisLoading || ["QUEUED", "RUNNING"].includes(analysisStatus?.status)
            ? "Analysis in progress..."
            : Number(analysisStatus?.artifactVersion) === artifactVersion &&
              analysisStatus?.status === "COMPLETED"
              ? "Analysis complete"
              : analysisStatus?.status === "PARTIAL"
                ? "Retry partial analysis"
              : "Run OCR and analysis"}
        </button>
        {["QUEUED", "RUNNING"].includes(analysisStatus?.status) && (
          <button
            type="button"
            className="button"
            onClick={cancelIDRAnalysis}
            disabled={!analysisStatus?.runId || analysisCancelLoading}
          >
            {analysisCancelLoading ? "Cancelling..." : "Cancel analysis"}
          </button>
        )}
        {analysisStatus?.status === "CANCELLED" && (
          <small>
            Analysis cancelled. An already-running provider request may have completed, but its
            result was not saved.
          </small>
        )}
        </div>
      </section>
    </main>
  );
}
